import type { ReactNode } from "react";
import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { rememberOrg } from "../lib/identity.ts";
import { PurchaseBanner } from "./purchase-banner.tsx";
import { ChevronLeft, Home, LayoutGrid, ShoppingCart, User } from "lucide-react";

import { useCart, useMe } from "../api/queries.ts";
import { cn } from "@/lib/utils";

/**
 * ═══════════════════════════════════════════════════════════════
 *  手机端外壳
 * ═══════════════════════════════════════════════════════════════
 *
 * 🔴 **这个平台的前台和商家端都是手机网页，不是"能在手机上打开的桌面站"。**
 *    买家在工地、在建材市场用手机看；分销商是装修师傅，他一天到晚在外面跑。
 *    所以宽度**恒定** —— 不做「窄屏一套宽屏一套」的响应式切换。
 *    以前后台是整屏桌面版，从商城点进去宽度会突变，看着像两个网站。
 *
 * ⚠️ 桌面上打开时**居中并保持手机宽度**，两侧留灰底。
 *    链接会被从微信复制到电脑打开 —— 那时候商品卡不该被拉成整屏那么大。
 */

/** 手机内容宽度。所有页面共用这一个值，别在页面里另写 max-w。 */
export const SHELL = "mx-auto w-full max-w-[480px]";

/**
 * 每个页面的**上级**。
 *
 * 🔴 用户从二维码扫进来、微信里点进来、粘贴地址打开时，当前页就是浏览器会话的
 *    **第一页** —— 按返回没有站内的上一页可退。查这张表跳到上级，
 *    而不是把人退出网站。
 *
 * ⚠️ 想的是「**他直接落在这一页时，最该去哪**」，不是「他通常从哪来」。
 *    比如「我的定价」的上级是商家端而不是首页 —— 回首页等于把装修师傅
 *    扔回买家的商城。
 *
 * ⚠️ **集中放在这里，不让每个页面自己声明。**
 *    19 个页面各写一遍的话，加新页时必然有人忘 ——
 *    而"忘了"的表现是把用户退出网站，很难被发现。
 *
 * ⚠️ 前缀匹配，**长的必须排在短的前面**：
 *    `/orders/123` 要先命中 `/orders/` 才不会被 `/orders` 抢走。
 */
const PARENT_OF: readonly (readonly [string, string])[] = [
  // ── 买家侧 ──
  ["/orders/", "/orders"],
  ["/orders", "/me"],
  ["/addresses", "/me"],
  ["/security", "/me"],
  ["/messages", "/me"],
  ["/cases/", "/cases"],
  ["/cases", "/"],
  ["/products/", "/products"],
  ["/checkout", "/cart"],
  ["/pay", "/orders"],
  ["/shop/", "/"],
  ["/login", "/"],
  ["/i/", "/"],
  // ── 商家侧 ──
  /**
   * ⚠️ 上级是「我的」而不是商城首页 —— 商家端已经并进「我的」的身份切换里，
   *    回首页等于把装修师傅扔回买家的商城，他还得再切一次身份。
   * ⚠️ `/merchant` 和 `/restock` 现在只是重定向页，**绝不能当上级** ——
   *    回到它们会立刻又被重定向走，看起来就是"返回键点了没反应"。
   */
  ["/my-pricing", "/me"],
  ["/team", "/me"],
  ["/earnings", "/me"],
  ["/withdraw", "/me"],
  ["/coupons", "/me"],
  ["/seller-orders", "/me"],
  ["/invite-share", "/me"],
  ["/shop-settings", "/me"],
  ["/shop-categories", "/me"],
  ["/my-products", "/me"],
  ["/ship", "/me"],
  ["/merchant", "/me"],
  ["/restock", "/me"],
];

function parentOf(pathname: string): string {
  for (const [prefix, parent] of PARENT_OF) {
    if (pathname.startsWith(prefix)) return parent;
  }
  return "/";
}

/**
 * 返回上一页。
 *
 * 🔴 **`history.back()` 单独用是不可靠的。**
 *    这个平台的链接是**到处传**的：二维码扫进来、微信里点进来、
 *    粘贴地址打开、搜索引擎点进来 —— 这些情况下当前页就是浏览器会话的
 *    **第一页**，`back()` 会把人退到站外（在微信里就是直接关掉网页）。
 *    而他只是想返回上一级。
 *
 * 🔴 **也不能用 `history.length`** —— 它只增不减，用户在站内前进后退几次
 *    之后这个数永远大于 1，判断不出"还有没有站内的上一页"。
 *
 * ✅ 用路由库自己维护的 `__TSR_index`：**它是当前页在本次会话里的序号**。
 *    等于 0 就说明这是第一页，退回去必然出站 —— 这时改成跳到声明的上级。
 *
 * @param fallback 覆盖默认上级。⚠️ 一般不用传 —— 默认按 `PARENT_OF` 查。
 */
function useGoBack(fallback?: string) {
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  return () => {
    /**
     * 🔴 **回「我的」之前，先把"他在经营哪一家店"记下来。**
     *    商家的二级页都带 `orgId`。不记的话，直接打开「我的定价」
     *    （书签、二维码、转发的链接）再按返回，会落到**买家视角**的「我的」——
     *    刚才还在改价，一返回变成订单和收货地址，还得自己再切一次身份。
     *
     * ⚠️ **直接读地址栏，不走路由库的 `location.search`。**
     *    这里是布局组件，拿到的是**它自己**那一层的 search，
     *    未必是当前页面的 —— 真机上就是这么静默拿到 undefined 的。
     *    地址栏是唯一一份不会走样的真相。
     */
    rememberOrg(Number(new URLSearchParams(window.location.search).get("orgId")) || null);

    const idx = (window.history.state as { __TSR_index?: number } | null)?.__TSR_index;

    /**
     * ⚠️ `idx` 认不出来时（老浏览器、被别的脚本改过 state）当成"没有来路"处理 ——
     *    宁可多跳一层，也不能冒把人退出网站的险。
     */
    if (typeof idx === "number" && idx > 0) {
      window.history.back();
      return;
    }
    navigate({ to: fallback ?? parentOf(pathname) });
  };
}

const TABS = [
  { to: "/", label: "首页", icon: Home },
  { to: "/products", label: "分类", icon: LayoutGrid },
  { to: "/cart", label: "购物车", icon: ShoppingCart },
  { to: "/me", label: "我的", icon: User },
] as const;

function TabBar() {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const { data: me } = useMe();
  const { data: cart } = useCart(Boolean(me));
  const cartCount = cart?.groups.reduce((n, g) => n + g.lines.length, 0) ?? 0;

  return (
    <nav
      className={cn(
        "fixed inset-x-0 bottom-0 z-30 grid grid-cols-4 border-t border-border bg-background/95 backdrop-blur-lg pb-safe",
        SHELL,
      )}
    >
      {TABS.map(({ to, label, icon: Icon }) => {
        const active = to === "/" ? path === "/" : path.startsWith(to);
        return (
          <Link
            key={to}
            to={to}
            className={cn(
              "flex h-14 flex-col items-center justify-center gap-1 text-[10px] transition-colors",
              active ? "text-primary" : "text-muted-foreground",
            )}
          >
            <span className="relative">
              <Icon className={cn("size-[22px]", active && "stroke-[2.25]")} />
              {to === "/cart" && cartCount > 0 && (
                <span className="absolute -top-1.5 -right-2.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-medium text-white tabular-nums">
                  {cartCount > 99 ? "99+" : cartCount}
                </span>
              )}
            </span>
            {label}
          </Link>
        );
      })}
    </nav>
  );
}

/**
 * 页面最外层。
 *
 * ⚠️ 两层结构是有意的：外层铺满深灰（桌面上两侧的留白），
 *    内层是手机内容区。少了外层，桌面上两侧会是刺眼的纯白。
 */
/**
 * 会按「进货价」显示的页面 —— 只有这些页面需要那条提示。
 *
 * ⚠️ 商家自己的后台页（定价、订单、收益…）不出，那里本来就不是零售价，
 *    到处贴一条提示反而成了噪音。
 */
export const PRICED_PAGES = ["/products", "/cart", "/checkout", "/shop/", "/cases"];

/** 这个路径下的价格是"按当前身份算出来的"，需要那条提示条。两套版面共用这一条规则。 */
export function isPricedPage(pathname: string): boolean {
  return pathname === "/" || PRICED_PAGES.some((p) => pathname.startsWith(p));
}

function Frame({ children }: { children: ReactNode }) {
  const pathname = useRouterState({ select: (st) => st.location.pathname });
  const showPurchase = isPricedPage(pathname);

  return (
    <div className="min-h-dvh bg-neutral-200/60">
      <div className={cn("min-h-dvh bg-muted/40", SHELL)}>
        {/*
          🔴 **换了价格体系就必须说出来。**
             切到商家身份后整个商城显示的是进货价，比零售价低一大截 ——
             不说的话他会以为平台降价了。
        */}
        {showPurchase && <PurchaseBanner />}
        {children}
      </div>
    </div>
  );
}

/** 带底部导航的一级页（首页、分类、购物车、我的）。 */
export function TabPage({ children }: { children: ReactNode }) {
  return (
    <>
      <Frame>
        <div className="pb-[calc(3.5rem+env(safe-area-inset-bottom))]">{children}</div>
      </Frame>
      <TabBar />
    </>
  );
}

/**
 * 带返回的二级页。
 *
 * @param footer 固定在底部的操作条（加入购物车、去结算、提交订单）。
 */
export function SubPage({
  title,
  children,
  footer,
  action,
  backTo,
}: {
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  /** 标题栏右侧的操作（如「管理」「筛选」）。 */
  action?: ReactNode;
  /**
   * 这一页的**上级**。
   *
   * 🔴 用户从二维码/微信/粘贴地址直接进来时没有站内上一页 ——
   *    这时返回键跳到这里，而不是把他退出网站。
   *    不填就回首页。**每个二级页都该填**：回首页对
   *    「商家端 → 我的定价」这种深层页来说等于把人扔回起点。
   */
  backTo?: string;
}) {
  const goBack = useGoBack(backTo);

  return (
    <Frame>
      <header
        className={cn(
          "sticky top-0 z-20 flex h-12 items-center gap-1 border-b border-border bg-background/90 px-1 backdrop-blur-lg pt-safe-2",
        )}
      >
        <button
          className="flex size-11 items-center justify-center text-foreground active:opacity-60"
          onClick={goBack}
          aria-label="返回"
        >
          <ChevronLeft className="size-[22px]" />
        </button>
        <h1 className="min-w-0 flex-1 truncate text-[16px] font-medium">{title}</h1>
        {action && <div className="flex items-center pr-2">{action}</div>}
      </header>

      <div className={footer ? "pb-[calc(4.5rem+env(safe-area-inset-bottom))]" : ""}>
        {children}
      </div>

      {footer && (
        <div
          className={cn(
            /*
              ⚠️ 用 `pb-safe-3` 而不是 `pb-2.5 pb-safe` —— 后者两条都是
                 padding-bottom，没有 home indicator 的设备上安全区是 0、
                 把 pb-2.5 覆盖成 0，按钮会贴死在屏幕最下沿。
            */
            "fixed inset-x-0 bottom-0 z-30 border-t border-border bg-background px-4 pt-2.5 pb-safe-3",
            SHELL,
          )}
        >
          {footer}
        </div>
      )}
    </Frame>
  );
}

/**
 * 商家端 / 后台的手机版外壳。
 *
 * 🔴 **分销商是装修师傅，他用手机。** 以前这里是整屏桌面版，
 *    从商城点进「商家端」宽度会突然变整屏 —— 看着像两个网站。
 *    现在跟前台共用同一个 `SHELL`，宽度恒定。
 *
 * @param nav 顶部横向标签。⚠️ 手机上放不下侧边栏，横滚标签是唯一可行的形态。
 */
export function DeskPage({
  title,
  subtitle,
  nav,
  children,
  activeTab,
  backTo,
}: {
  title: string;
  subtitle?: string;
  nav?: { to: string; label: string; search?: Record<string, unknown> }[];
  children: ReactNode;
  /** 用 search 参数分页签时，靠它判断选中（光比 pathname 不够）。 */
  activeTab?: string;
  /** 同 `SubPage.backTo` —— 没有站内上一页时跳这里。 */
  backTo?: string;
}) {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const goBack = useGoBack(backTo);

  return (
    <Frame>
      <header className="sticky top-0 z-20 border-b border-border bg-background/90 backdrop-blur-lg pt-safe-2">
        <div className="flex h-12 items-center gap-1 px-1">
          <button
            className="flex size-11 items-center justify-center text-foreground active:opacity-60"
            onClick={goBack}
            aria-label="返回"
          >
            <ChevronLeft className="size-[22px]" />
          </button>
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-[16px] leading-tight font-medium">{title}</h1>
            {subtitle && (
              <p className="truncate text-[11px] leading-tight text-muted-foreground">{subtitle}</p>
            )}
          </div>
          <Link to="/" className="pr-3 text-[13px] text-muted-foreground active:opacity-60">
            回商城
          </Link>
        </div>

        {nav && nav.length > 0 && (
          <nav className="no-scrollbar flex gap-1 overflow-x-auto px-2 pb-1.5">
            {nav.map((n) => {
              const tab = (n.search as { tab?: string } | undefined)?.tab;
              const active = activeTab ? tab === activeTab : path === n.to;
              return (
                <Link
                  key={n.to + JSON.stringify(n.search ?? {})}
                  to={n.to}
                  search={(n.search ?? {}) as never}
                  className={cn(
                    "shrink-0 rounded-full px-3 py-1.5 text-[13px] whitespace-nowrap transition-colors",
                    active
                      ? "bg-primary text-primary-foreground font-medium"
                      : "bg-secondary text-muted-foreground",
                  )}
                >
                  {n.label}
                </Link>
              );
            })}
          </nav>
        )}
      </header>

      <div className="px-3 py-3">{children}</div>
    </Frame>
  );
}
